/**
 * ============================================================================
 * USE ABORT CONTROLLER HOOK
 * ============================================================================
 * 
 * Custom hook for request cancellation
 */

import { useEffect, useRef, useCallback } from 'react';

/**
 * Abort controller hook
 * @returns {Object} { signal, abort, isAborted }
 */
export const useAbortController = () => {
  const abortControllerRef = useRef(null);

  /**
   * Create new abort controller
   */
  const createAbortController = useCallback(() => {
    // Abort previous if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    return abortControllerRef.current;
  }, []);

  /**
   * Get current signal
   */
  const getSignal = useCallback(() => {
    if (!abortControllerRef.current) {
      abortControllerRef.current = new AbortController();
    }
    return abortControllerRef.current.signal;
  }, []);

  /**
   * Abort current request
   */
  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  /**
   * Check if aborted
   */
  const isAborted = useCallback(() => {
    return abortControllerRef.current?.signal.aborted || false;
  }, []);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    signal: getSignal(),
    getSignal,
    abort,
    isAborted,
    createAbortController,
  };
};

export default useAbortController;
